import Checkbox from "@/components/Checkbox";
import Search from "@/components/Search";
import { CheckboxVariant, SearchVariant, Size } from "@ever-tutor/et-ui";
import type { FilterOption } from "../../constants/config/filter";
import { useState, useMemo } from "react";
import FilterActions from "./FilterActions";

interface MultiSelectFilterProps {
  options: FilterOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  onApply: () => void;
  onClear: () => void;
  searchPlaceholder?: string;
  showSearch?: boolean;
}

const MultiSelectFilter = ({
  options,
  selectedValues,
  onChange,
  onApply,
  onClear,
  searchPlaceholder = "Search...",
  showSearch = true,
}: MultiSelectFilterProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim() || !showSearch) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [options, searchQuery, showSearch]);

  // Membership checks run inside loops over server-sized option lists.
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const allSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selectedSet.has(opt.value));

  const handleToggle = (value: string) => {
    if (selectedSet.has(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      const visible = new Set(filteredOptions.map((opt) => opt.value));
      onChange(selectedValues.filter((v) => !visible.has(v)));
    } else {
      const newValues = [...selectedValues];
      const added = new Set(selectedValues);
      filteredOptions.forEach((opt) => {
        if (!added.has(opt.value)) {
          added.add(opt.value);
          newValues.push(opt.value);
        }
      });
      onChange(newValues);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {showSearch && (
        <Search
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={setSearchQuery}
          showClearButton={false}
          inputClassName="text-xs"
          height="40px"
          variant={SearchVariant.OUTLINE}
          size={Size.SM}
        />
      )}
      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2">
          <Checkbox
            checked={allSelected}
            onChange={handleSelectAll}
            variant={CheckboxVariant.SIMPLE}
          >
            Select All
          </Checkbox>
        </label>
        <div className="flex flex-col gap-1 max-h-60 overflow-auto">
          {filteredOptions.length === 0 && (
            <p role="status" className="p-2 text-sm text-gray-500">
              No matching options
            </p>
          )}
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              data-selected={selectedSet.has(option.value)}
              className={`et-filter-option hover:bg-gray-50 p-2 ${
                selectedSet.has(option.value) ? "bg-[#EDFFF8]" : ""
              }`}
            >
              <Checkbox
                checked={selectedSet.has(option.value)}
                onChange={() => handleToggle(option.value)}
                labelClassName="text-sm font-mulish text-[#252525]"
                variant={CheckboxVariant.SIMPLE}
              >
                {option.label}
              </Checkbox>
            </div>
          ))}
        </div>
      </div>
      <FilterActions onApply={onApply} onClear={onClear} />
    </div>
  );
};

export default MultiSelectFilter;
